import { X, Pin, ArrowUpRight } from "lucide-react"
import type { Message } from "./types"

interface PinnedMessagesPanelProps {
  messages: Message[]
  onClose: () => void
  onJumpTo: (messageId: string) => void
  onUnpin: (messageId: string) => void
}

export function PinnedMessagesPanel({ messages, onClose, onJumpTo, onUnpin }: PinnedMessagesPanelProps) {
  return (
    <div className="flex flex-col h-full bg-card rounded-xl border-l border-border">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <Pin className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Pinned Messages</h3>
          <span className="text-xs bg-primary/10 text-primary font-semibold px-2 py-0.5 rounded-full">
            {messages.length}
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full px-8 text-center gap-3">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
              <Pin className="w-5 h-5 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">No pinned messages</p>
            <p className="text-xs text-muted-foreground/60">Pin important messages to find them quickly</p>
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {messages.map((msg, idx) => (
              <div key={msg.id} className="px-4 py-3 hover:bg-muted/40 transition-colors group">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Pin className="w-3 h-3 text-primary shrink-0" />
                      <span className="text-xs font-semibold text-primary">
                        Pinned message #{idx + 1}
                      </span>
                    </div>
                    <p className="text-sm text-foreground line-clamp-3">{msg.text}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-xs text-muted-foreground">
                        {msg.isSentByMe ? "You" : msg.senderName ?? "Unknown"} · {msg.time}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <button
                      onClick={() => onJumpTo(msg.id)}
                      className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                      title="Jump to message"
                    >
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onUnpin(msg.id)}
                      className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                      title="Unpin"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
